// fetch /api/* with the device token as bearer. Same origin in production;
// vite's dev server proxies /api to the local API. In fixture mode the
// requests go to an in-memory server built from the Sheets snapshot instead.
import type { Violation } from "../lib/types";
import { getDeviceToken } from "../auth/platform";
import { fixtureMode } from "./mode";

export class ApiError extends Error {
  constructor(public status: number, message: string, public code: string, public violations?: Violation[], public locked?: string[]) {
    super(message);
  }
}

/** Fired on any 401 so the shell can show the sign-in page. */
export const UNAUTHORIZED_EVENT = "oc:unauthorized";

export async function api<T>(method: "GET" | "POST" | "PUT" | "DELETE", path: string, body?: unknown): Promise<T> {
  if (fixtureMode()) {
    const { fixtureRequest } = await import("../fixtures/server");
    const r = await fixtureRequest(method, path, body);
    if (r.status >= 400) throw toError(r.status, r.body);
    return r.body as T;
  }
  const res = await fetch(path, {
    method,
    headers: { Authorization: `Bearer ${getDeviceToken()}`, ...(body !== undefined ? { "Content-Type": "application/json" } : {}) },
    body: body !== undefined ? JSON.stringify(body) : undefined
  });
  const text = await res.text();
  let json: unknown = null;
  try { json = text ? JSON.parse(text) : null; } catch { json = { error: text || res.statusText, code: "server_error" }; }
  if (!res.ok) {
    if (res.status === 401) window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
    throw toError(res.status, json);
  }
  return json as T;
}

function toError(status: number, body: unknown): ApiError {
  const b = (body ?? {}) as { error?: string; code?: string; violations?: Violation[]; locked?: string[] };
  return new ApiError(status, b.error ?? `Request failed (${status})`, b.code ?? "server_error", b.violations, b.locked);
}

export const get = <T,>(path: string) => api<T>("GET", path);
export const post = <T,>(path: string, body?: unknown) => api<T>("POST", path, body ?? {});
export const put = <T,>(path: string, body?: unknown) => api<T>("PUT", path, body ?? {});
